import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VideocamIcon from "@mui/icons-material/Videocam";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ImageIcon from "@mui/icons-material/Image";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import io from "socket.io-client";

import {
  captureNodeSnapshot,
  fetchCameraNodes,
  fetchDevices,
  fetchMyCameraSessions,
  fetchNodeLatestSnapshot,
  registerCameraNode,
  sendCameraHeartbeat,
  sendCameraIntercomChunk,
  startCameraStream,
  startCameraIntercom,
  stopAllMyCameraSessions,
  stopCameraIntercom,
  stopCameraSession,
} from "../../services/cameraV2/cameraV2Service";

const HEARTBEAT_INTERVAL_MS = 20000;
const INTERCOM_SAMPLE_RATE = 8000;
const INTERCOM_FLUSH_MS = 250;
const AUDIO_SAMPLE_RATE = 8000;
const JT1078_AUDIO_PT_PCMU = 0;
const JT1078_AUDIO_PT_PCMA = 8;

function asId(value) {
  if (!value) return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
}

function normalizeError(error) {
  return (
    error?.response?.data?.msg ||
    error?.response?.data?.error ||
    error?.message ||
    "Request failed"
  );
}

function getCameraNodeIdFromSession(session) {
  if (!session) return "";
  return asId(session.cameraNodeId);
}

function isMjpegPlaybackUrl(url) {
  const playbackUrl = String(url || "").trim();
  return /\.mjpg(\?|$)/i.test(playbackUrl) || /\/video\//i.test(playbackUrl);
}

function getApiBaseUrl() {
  const configured = String(process.env.REACT_APP_BACKEND_URL || "").trim();
  if (configured) {
    return configured.replace(/\/+$/g, "");
  }
  return `${window.location.origin.replace(/\/+$/g, "")}/api`;
}

function getRenderablePlaybackUrl(playbackUrl, sessionId) {
  const rawUrl = String(playbackUrl || "").trim();
  if (!rawUrl) return "";

  try {
    const parsed = new URL(rawUrl, window.location.origin);
    const securePage = window.location.protocol === "https:";
    if (!securePage || parsed.protocol !== "http:" || !sessionId) {
      return rawUrl;
    }

    const token = localStorage.getItem("token") || "";
    if (!token) {
      return rawUrl;
    }

    return `${getApiBaseUrl()}/v2/camera/sessions/${encodeURIComponent(
      sessionId
    )}/playback?token=${encodeURIComponent(token)}`;
  } catch (_) {
    return rawUrl;
  }
}

function isCameraCapableDevice(device) {
  const type = String(device?.deviceType || "").toLowerCase();
  const protocol = String(device?.protocol || "").toLowerCase();
  const model = String(device?.model || "").toLowerCase();
  return Boolean(
    device?.imei ||
      device?.terminalId ||
      device?.msdn ||
      type.includes("jt808") ||
      protocol.includes("jt808") ||
      model.includes("jt808") ||
      model.includes("bsj")
  );
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

const initialForm = {
  deviceId: "",
  name: "",
  channelId: 1,
};

function concatInt16Chunks(chunks) {
  const total = chunks.reduce((sum, chunk) => sum + (chunk?.length || 0), 0);
  const merged = new Int16Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    if (!chunk?.length) continue;
    merged.set(chunk, offset);
    offset += chunk.length;
  }
  return merged;
}

function downsampleToInt16(source, inputRate, outputRate = INTERCOM_SAMPLE_RATE) {
  if (!source?.length || !inputRate) {
    return new Int16Array(0);
  }
  if (outputRate >= inputRate) {
    const out = new Int16Array(source.length);
    for (let i = 0; i < source.length; i += 1) {
      const sample = Math.max(-1, Math.min(1, source[i]));
      out[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    }
    return out;
  }

  const ratio = inputRate / outputRate;
  const length = Math.max(1, Math.round(source.length / ratio));
  const out = new Int16Array(length);
  let offsetResult = 0;
  let offsetBuffer = 0;

  while (offsetResult < out.length) {
    const nextOffset = Math.min(source.length, Math.round((offsetResult + 1) * ratio));
    let accum = 0;
    let count = 0;
    for (let i = offsetBuffer; i < nextOffset; i += 1) {
      accum += source[i];
      count += 1;
    }
    const sample = count > 0 ? accum / count : source[Math.min(offsetBuffer, source.length - 1)] || 0;
    const clamped = Math.max(-1, Math.min(1, sample));
    out[offsetResult] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
    offsetResult += 1;
    offsetBuffer = nextOffset;
  }

  return out;
}

function int16ToBase64(int16Array) {
  if (!int16Array?.length) return "";
  const bytes = new Uint8Array(int16Array.buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function getAudioSocketBaseUrl(playbackUrl) {
  try {
    const parsed = new URL(String(playbackUrl || "").trim());
    return parsed.origin;
  } catch {
    return "";
  }
}

function getAudioSubscriptionTarget(playbackUrl, fallbackNode) {
  const url = String(playbackUrl || "").trim();
  if (!url) return null;

  const withChannel = url.match(/\/video\/([^/]+)\/(\d+)\.mjpg(?:\?|$)/i);
  if (withChannel) {
    return {
      deviceId: decodeURIComponent(withChannel[1]),
      channel: Number(withChannel[2]) || Number(fallbackNode?.channelId) || 1,
    };
  }

  const singleChannel = url.match(/\/video\/([^/]+)\.mjpg(?:\?|$)/i);
  if (singleChannel) {
    return {
      deviceId: decodeURIComponent(singleChannel[1]),
      channel: Number(fallbackNode?.channelId) || 1,
    };
  }

  const fallbackDeviceId = String(fallbackNode?.terminalId || "").trim();
  if (!fallbackDeviceId) return null;
  return {
    deviceId: fallbackDeviceId,
    channel: Number(fallbackNode?.channelId) || 1,
  };
}

function alawToLinearSample(value) {
  let a = value ^ 0x55;
  let t = (a & 0x0f) << 4;
  const seg = (a & 0x70) >> 4;
  switch (seg) {
    case 0:
      t += 8;
      break;
    case 1:
      t += 0x108;
      break;
    default:
      t += 0x108;
      t <<= seg - 1;
      break;
  }
  return (a & 0x80) ? t : -t;
}

function ulawToLinearSample(value) {
  const u = (~value) & 0xff;
  let t = ((u & 0x0f) << 3) + 0x84;
  t <<= (u & 0x70) >> 4;
  return (u & 0x80) ? (0x84 - t) : (t - 0x84);
}

function decodeG711Payload(payloadBase64, payloadType) {
  const binary = window.atob(String(payloadBase64 || ""));
  if (!binary.length) {
    return new Float32Array(0);
  }

  const out = new Float32Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    const byte = binary.charCodeAt(i) & 0xff;
    const sample =
      Number(payloadType) === JT1078_AUDIO_PT_PCMU
        ? ulawToLinearSample(byte)
        : alawToLinearSample(byte);
    out[i] = Math.max(-1, Math.min(1, sample / 32768));
  }
  return out;
}

export default function CameraControlPortal() {
  const [devices, setDevices] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [sessionsByNode, setSessionsByNode] = useState({});

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionNodeId, setActionNodeId] = useState("");
  const [captureNodeId, setCaptureNodeId] = useState("");

  const [form, setForm] = useState(initialForm);
  const [selectedNodeId, setSelectedNodeId] = useState("");
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [snapshotByNode, setSnapshotByNode] = useState({});
  const [intercomNodeId, setIntercomNodeId] = useState("");
  const [intercomActive, setIntercomActive] = useState(false);
  const [intercomSending, setIntercomSending] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const intercomRef = useRef({
    nodeId: "",
    mediaStream: null,
    audioContext: null,
    source: null,
    processor: null,
    gainNode: null,
    flushTimer: null,
    pendingChunks: [],
    sendInFlight: false,
  });
  const audioPlaybackRef = useRef({
    socket: null,
    audioContext: null,
    nextPlaybackTime: 0,
    subscription: null,
  });

  const deviceById = useMemo(() => {
    const map = new Map();
    for (const device of devices) {
      map.set(asId(device._id), device);
    }
    return map;
  }, [devices]);

  const cameraDevices = useMemo(() => devices.filter(isCameraCapableDevice), [devices]);

  const visibleNodes = useMemo(() => {
    if (!selectedDeviceId) return nodes;
    return nodes.filter((node) => asId(node.deviceId) === selectedDeviceId);
  }, [nodes, selectedDeviceId]);

  const selectedNode = useMemo(
    () => visibleNodes.find((node) => asId(node._id) === selectedNodeId) || null,
    [visibleNodes, selectedNodeId]
  );

  const selectedSession = useMemo(() => {
    if (!selectedNode) return null;
    return sessionsByNode[asId(selectedNode._id)] || null;
  }, [selectedNode, sessionsByNode]);

  const selectedSnapshot = useMemo(() => {
    if (!selectedNode) return null;
    return snapshotByNode[asId(selectedNode._id)]?.data || null;
  }, [selectedNode, snapshotByNode]);

  const activeSessionNodes = useMemo(() => {
    return visibleNodes
      .map((node) => {
        const nodeId = asId(node._id);
        const session = sessionsByNode[nodeId];
        if (!session?.sessionId) return null;

        return {
          node,
          nodeId,
          session,
          device: deviceById.get(asId(node.deviceId)),
        };
      })
      .filter(Boolean);
  }, [visibleNodes, sessionsByNode, deviceById]);

  const loadPortalData = useCallback(async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setErrorMessage("");

    try {
      const [devicesData, nodesData, sessionsData] = await Promise.all([
        fetchDevices(),
        fetchCameraNodes(),
        fetchMyCameraSessions(),
      ]);

      setDevices(devicesData);
      setNodes(nodesData);

      const nextSessions = {};
      for (const session of sessionsData) {
        const nodeId = getCameraNodeIdFromSession(session);
        if (!nodeId) continue;

        const existing = nextSessions[nodeId];
        const existingTime = existing?.startedAt ? new Date(existing.startedAt).getTime() : 0;
        const nextTime = session?.startedAt ? new Date(session.startedAt).getTime() : 0;

        if (!existing || nextTime >= existingTime) {
          nextSessions[nodeId] = session;
        }
      }
      setSessionsByNode(nextSessions);

      setSelectedDeviceId((prev) => {
        if (prev && nodesData.some((node) => asId(node.deviceId) === prev)) {
          return prev;
        }
        if (form.deviceId && nodesData.some((node) => asId(node.deviceId) === form.deviceId)) {
          return form.deviceId;
        }
        return nodesData.length ? asId(nodesData[0].deviceId) : "";
      });

      setSelectedNodeId((prev) => {
        if (!prev && nodesData.length > 0) {
          return asId(nodesData[0]._id);
        }
        if (prev && !nodesData.some((node) => asId(node._id) === prev)) {
          return nodesData.length ? asId(nodesData[0]._id) : "";
        }
        return prev;
      });
    } catch (error) {
      setErrorMessage(normalizeError(error));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [form.deviceId]);

  const loadNodeSnapshot = useCallback(async (nodeId) => {
    const key = asId(nodeId);
    if (!key) return null;

    setSnapshotByNode((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), loading: true, error: "" },
    }));

    try {
      const result = await fetchNodeLatestSnapshot(key);
      const snapshot = result?.snapshot || null;
      setSnapshotByNode((prev) => ({
        ...prev,
        [key]: { loading: false, error: "", data: snapshot },
      }));
      return snapshot;
    } catch (error) {
      setSnapshotByNode((prev) => ({
        ...prev,
        [key]: { ...(prev[key] || {}), loading: false, error: normalizeError(error) },
      }));
      return null;
    }
  }, []);

  const pollNodeSnapshot = useCallback(
    async (nodeId, attempts = 5, waitMs = 1600) => {
      for (let i = 0; i < attempts; i += 1) {
        const snapshot = await loadNodeSnapshot(nodeId);
        if (snapshot?.resolvedUrl || snapshot?.url) return snapshot;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, waitMs));
      }
      return null;
    },
    [loadNodeSnapshot]
  );

  const cleanupIntercomCapture = useCallback(() => {
    const current = intercomRef.current;
    if (current.flushTimer) {
      clearInterval(current.flushTimer);
    }
    try {
      current.processor?.disconnect();
    } catch {}
    try {
      current.source?.disconnect();
    } catch {}
    try {
      current.gainNode?.disconnect();
    } catch {}
    if (current.mediaStream) {
      current.mediaStream.getTracks().forEach((track) => track.stop());
    }
    if (current.audioContext && current.audioContext.state !== "closed") {
      current.audioContext.close().catch(() => {});
    }
    intercomRef.current = {
      nodeId: "",
      mediaStream: null,
      audioContext: null,
      source: null,
      processor: null,
      gainNode: null,
      flushTimer: null,
      pendingChunks: [],
      sendInFlight: false,
    };
  }, []);

  const cleanupAudioPlayback = useCallback(() => {
    const current = audioPlaybackRef.current;
    if (current.socket && current.subscription) {
      current.socket.emit("camera-audio-unsubscribe", current.subscription);
    }
    try {
      current.socket?.disconnect();
    } catch {}
    if (current.audioContext && current.audioContext.state !== "closed") {
      current.audioContext.close().catch(() => {});
    }
    audioPlaybackRef.current = {
      socket: null,
      audioContext: null,
      nextPlaybackTime: 0,
      subscription: null,
    };
  }, []);

  const handleStopIntercom = useCallback(
    async (node) => {
      const nodeId = asId(node?._id || intercomNodeId);
      cleanupIntercomCapture();
      setIntercomSending(false);
      setIntercomActive(false);
      setIntercomNodeId("");
      if (!nodeId) return;
      try {
        await stopCameraIntercom(nodeId);
      } catch (error) {
        setErrorMessage(normalizeError(error));
      }
    },
    [cleanupIntercomCapture, intercomNodeId]
  );

  const handleStartIntercom = useCallback(
    async (node) => {
      const nodeId = asId(node?._id);
      if (!nodeId) return;
      if (!navigator?.mediaDevices?.getUserMedia) {
        setErrorMessage("Browser microphone capture is not supported.");
        return;
      }

      setErrorMessage("");
      setSuccessMessage("");
      setIntercomSending(true);

      try {
        await startCameraIntercom(nodeId);
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            sampleRate: INTERCOM_SAMPLE_RATE,
            noiseSuppression: true,
            echoCancellation: true,
          },
        });

        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioCtx();
        await audioContext.resume();

        const source = audioContext.createMediaStreamSource(stream);
        const processor = audioContext.createScriptProcessor(4096, 1, 1);
        const gainNode = audioContext.createGain();
        gainNode.gain.value = 0;

        const state = {
          nodeId,
          mediaStream: stream,
          audioContext,
          source,
          processor,
          gainNode,
          flushTimer: null,
          pendingChunks: [],
          sendInFlight: false,
        };

        processor.onaudioprocess = (event) => {
          const input = event.inputBuffer.getChannelData(0);
          const pcm = downsampleToInt16(input, audioContext.sampleRate, INTERCOM_SAMPLE_RATE);
          if (pcm.length > 0) {
            state.pendingChunks.push(pcm);
          }
        };

        source.connect(processor);
        processor.connect(gainNode);
        gainNode.connect(audioContext.destination);

        state.flushTimer = window.setInterval(async () => {
          if (state.sendInFlight || state.pendingChunks.length === 0) return;
          state.sendInFlight = true;
          try {
            const merged = concatInt16Chunks(state.pendingChunks.splice(0, state.pendingChunks.length));
            const audioBase64 = int16ToBase64(merged);
            if (audioBase64) {
              await sendCameraIntercomChunk(nodeId, {
                audioBase64,
                sampleRate: INTERCOM_SAMPLE_RATE,
              });
            }
          } catch (error) {
            setErrorMessage(normalizeError(error));
            handleStopIntercom(node).catch(() => {});
          } finally {
            state.sendInFlight = false;
          }
        }, INTERCOM_FLUSH_MS);

        intercomRef.current = state;
        setIntercomNodeId(nodeId);
        setIntercomActive(true);
        setSuccessMessage("Two-way intercom active. Mic audio camera ko send ho raha hai.");
      } catch (error) {
        cleanupIntercomCapture();
        try {
          await stopCameraIntercom(nodeId);
        } catch {}
        setErrorMessage(normalizeError(error));
      } finally {
        setIntercomSending(false);
      }
    },
    [cleanupIntercomCapture, handleStopIntercom]
  );

  useEffect(() => {
    loadPortalData();
  }, [loadPortalData]);

  useEffect(() => () => {
    cleanupIntercomCapture();
  }, [cleanupIntercomCapture]);

  useEffect(() => () => {
    cleanupAudioPlayback();
  }, [cleanupAudioPlayback]);

  useEffect(() => {
    const timer = setInterval(() => {
      loadPortalData({ silent: true });
    }, 15000);
    return () => clearInterval(timer);
  }, [loadPortalData]);

  useEffect(() => {
    const sessionList = Object.values(sessionsByNode || {}).filter(
      (session) => session && session.sessionId
    );

    if (sessionList.length === 0) return undefined;

    const timer = setInterval(() => {
      sessionList.forEach((session) => {
        sendCameraHeartbeat(session.sessionId).catch(() => {});
      });
    }, HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [sessionsByNode]);

  useEffect(() => {
    setSelectedNodeId((prev) => {
      if (prev && visibleNodes.some((node) => asId(node._id) === prev)) {
        return prev;
      }
      return visibleNodes.length ? asId(visibleNodes[0]._id) : "";
    });
  }, [visibleNodes]);

  useEffect(() => {
    if (!selectedNode) return;
    const deviceId = asId(selectedNode.deviceId);
    if (deviceId && deviceId !== selectedDeviceId) {
      setSelectedDeviceId(deviceId);
    }
  }, [selectedNode, selectedDeviceId]);

  useEffect(() => {
    if (visibleNodes.length === 0) return;
    visibleNodes.slice(0, 8).forEach((node) => {
      const nodeId = asId(node._id);
      if (!snapshotByNode[nodeId]) {
        loadNodeSnapshot(nodeId);
      }
    });
  }, [visibleNodes, snapshotByNode, loadNodeSnapshot]);

  useEffect(() => {
    if (!selectedNode) return;
    loadNodeSnapshot(asId(selectedNode._id));
  }, [selectedNode, loadNodeSnapshot]);

  useEffect(() => {
    const playbackUrl = String(selectedSession?.playbackUrl || "").trim();
    const socketBaseUrl = getAudioSocketBaseUrl(playbackUrl);
    const subscription = getAudioSubscriptionTarget(playbackUrl, selectedNode);

    cleanupAudioPlayback();

    if (!selectedSession?.sessionId || !socketBaseUrl || !subscription) {
      return undefined;
    }

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) {
      return undefined;
    }

    const audioContext = new AudioCtx({ sampleRate: AUDIO_SAMPLE_RATE });
    const socket = io(socketBaseUrl, {
      transports: ["websocket", "polling"],
    });

    const state = {
      socket,
      audioContext,
      nextPlaybackTime: 0,
      subscription,
    };
    audioPlaybackRef.current = state;

    socket.on("connect", () => {
      socket.emit("camera-audio-subscribe", subscription);
    });

    socket.on("camera-audio-frame", (frame) => {
      if (
        String(frame?.deviceId || "") !== String(subscription.deviceId) ||
        Number(frame?.channel || 0) !== Number(subscription.channel || 0)
      ) {
        return;
      }

      const payloadType = Number(frame?.payloadType);
      if (![JT1078_AUDIO_PT_PCMA, JT1078_AUDIO_PT_PCMU].includes(payloadType)) {
        return;
      }

      const samples = decodeG711Payload(frame?.payloadBase64 || "", payloadType);
      if (!samples.length) {
        return;
      }

      if (audioContext.state === "suspended") {
        audioContext.resume().catch(() => {});
      }

      const buffer = audioContext.createBuffer(1, samples.length, AUDIO_SAMPLE_RATE);
      buffer.copyToChannel(samples, 0);

      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.destination);

      const startAt = Math.max(audioContext.currentTime + 0.05, state.nextPlaybackTime || 0);
      source.start(startAt);
      state.nextPlaybackTime = startAt + buffer.duration;
      source.onended = () => {
        try {
          source.disconnect();
        } catch {}
      };
    });

    return () => {
      cleanupAudioPlayback();
    };
  }, [cleanupAudioPlayback, selectedNode, selectedSession]);

  const handleFormChange = (field) => (event) => {
    const value = event?.target?.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRegisterNode = async (event, channelList = [Number(form.channelId) || 1]) => {
    event?.preventDefault?.();
    setErrorMessage("");
    setSuccessMessage("");

    const trimmedName = String(form.name || "").trim();
    if (!form.deviceId || !trimmedName) {
      setErrorMessage("Device aur camera name required hai.");
      return;
    }
    const channels = Array.from(
      new Set((channelList || []).map((ch) => Number(ch)).filter((ch) => Number.isInteger(ch) && ch > 0))
    );
    if (channels.length === 0) {
      setErrorMessage("Valid channel required.");
      return;
    }

    setSubmitting(true);
    try {
      const createdIds = [];
      for (const channelId of channels) {
        const payload = {
          deviceId: form.deviceId,
          name: channels.length > 1 ? `${trimmedName} CH${channelId}` : trimmedName,
          mode: "jt808",
          channelId,
          streamType: 1,
          dataType: 0,
        };
        // eslint-disable-next-line no-await-in-loop
        const registered = await registerCameraNode(payload);
        createdIds.push(asId(registered?._id));
      }

      setSuccessMessage(
        channels.length > 1
          ? `Camera nodes registered for CH${channels.join(", CH")}.`
          : `Camera node registered for CH${channels[0]}.`
      );
      setForm((prev) => ({
        ...prev,
        name: "",
      }));

      await loadPortalData({ silent: true });
      const lastNodeId = createdIds.filter(Boolean).slice(-1)[0] || "";
      if (lastNodeId) {
        setSelectedNodeId(lastNodeId);
      }
      if (form.deviceId) {
        setSelectedDeviceId(form.deviceId);
      }
    } catch (error) {
      setErrorMessage(normalizeError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartStream = async (nodeId) => {
    setActionNodeId(nodeId);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const result = await startCameraStream(nodeId);
      const session = result?.session || result;
      const cameraNodeId = getCameraNodeIdFromSession(session) || asId(nodeId);

      if (cameraNodeId && session?.sessionId) {
        setSessionsByNode((prev) => ({ ...prev, [cameraNodeId]: session }));
      }

      setSelectedNodeId(asId(nodeId));
      const node = nodes.find((item) => asId(item._id) === asId(nodeId));
      if (node) {
        setSelectedDeviceId(asId(node.deviceId));
      }
      setSuccessMessage(result?.alreadyRunning ? "Stream already active hai." : "Stream start ho gaya.");
    } catch (error) {
      setErrorMessage(normalizeError(error));
    } finally {
      setActionNodeId("");
    }
  };

  const handleStopStream = async (nodeId) => {
    const nodeKey = asId(nodeId);
    const session = sessionsByNode[nodeKey];
    if (!session?.sessionId) return;

    setActionNodeId(nodeKey);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      if (intercomActive && intercomNodeId === nodeKey) {
        await handleStopIntercom({ _id: nodeKey });
      }
      await stopCameraSession(session.sessionId);
      setSessionsByNode((prev) => {
        const next = { ...prev };
        delete next[nodeKey];
        return next;
      });
      setSuccessMessage("Stream stop ho gaya.");
    } catch (error) {
      setErrorMessage(normalizeError(error));
    } finally {
      setActionNodeId("");
    }
  };

  const handleStopAllStreams = async () => {
    setErrorMessage("");
    setSuccessMessage("");
    setActionNodeId("all");

    try {
      if (intercomActive && intercomNodeId) {
        await handleStopIntercom({ _id: intercomNodeId });
      }
      const result = await stopAllMyCameraSessions();
      const stopped = Number(result?.stopped || 0);
      setSessionsByNode({});
      setSuccessMessage(stopped > 0 ? `All streams stopped (${stopped}).` : "No active streams found.");
    } catch (error) {
      setErrorMessage(normalizeError(error));
    } finally {
      setActionNodeId("");
    }
  };

  const handleTakeSnapshot = async (node) => {
    const nodeId = asId(node?._id);
    if (!nodeId) return;

    setCaptureNodeId(nodeId);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await captureNodeSnapshot(nodeId, {
        channelId: Number(node?.channelId) || 1,
        command: 1,
        saveFlag: 1,
      });

      setSuccessMessage("Snapshot command sent. Latest image refresh ho rahi hai...");
      await pollNodeSnapshot(nodeId);
    } catch (error) {
      setErrorMessage(normalizeError(error));
    } finally {
      setCaptureNodeId("");
    }
  };

  const renderStreamPreview = () => {
    if (!selectedNode) {
      return (
        <Alert severity="info" variant="outlined">
          Camera node select karke stream start karo.
        </Alert>
      );
    }

    const playbackUrl = String(selectedSession?.playbackUrl || "").trim();
    const renderPlaybackUrl = getRenderablePlaybackUrl(playbackUrl, selectedSession?.sessionId);
    const isMjpeg = isMjpegPlaybackUrl(playbackUrl);
    const nodeId = asId(selectedNode._id);
    const snapshotState = snapshotByNode[nodeId] || {};
    const snapshot = selectedSnapshot;
    const snapshotUrl = String(snapshot?.resolvedUrl || snapshot?.url || "").trim();

    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Live Preview: {selectedNode.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Device: {deviceById.get(asId(selectedNode.deviceId))?.name || asId(selectedNode.deviceId)} | CH{" "}
              {selectedNode.channelId}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PhotoCameraIcon />}
              disabled={captureNodeId === nodeId}
              onClick={() => handleTakeSnapshot(selectedNode)}
            >
              Take Screenshot
            </Button>
            <Button
              size="small"
              color={intercomActive && intercomNodeId === nodeId ? "error" : "success"}
              variant={intercomActive && intercomNodeId === nodeId ? "contained" : "outlined"}
              startIcon={intercomActive && intercomNodeId === nodeId ? <MicOffIcon /> : <MicIcon />}
              disabled={intercomSending || (!selectedSession?.sessionId && !(intercomActive && intercomNodeId === nodeId))}
              onClick={() =>
                intercomActive && intercomNodeId === nodeId
                  ? handleStopIntercom(selectedNode)
                  : handleStartIntercom(selectedNode)
              }
            >
              {intercomActive && intercomNodeId === nodeId ? "Stop Talk" : "Push To Talk"}
            </Button>
            {playbackUrl ? (
              <Button size="small" component="a" href={playbackUrl} target="_blank" rel="noreferrer">
                Open URL
              </Button>
            ) : null}
          </Stack>
        </Stack>

        <Box
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "#0b1020",
            minHeight: 260,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!playbackUrl ? (
            <Stack spacing={0.8} alignItems="center" sx={{ color: "white" }}>
              <Chip size="small" label="OFFLINE" variant="outlined" sx={{ color: "white", borderColor: "grey.500" }} />
              <Typography variant="body2">Is node ka stream active nahi hai.</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Start button dabao, phir live feed yahan dikhegi.
              </Typography>
            </Stack>
          ) : isMjpeg ? (
            <img
              src={renderPlaybackUrl}
              alt={selectedNode.name}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <video
              src={renderPlaybackUrl}
              controls
              autoPlay
              muted
              playsInline
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          )}
        </Box>

        <Grid container spacing={1.2} sx={{ mt: 0.2 }}>
          <Grid item xs={12} md={7}>
            {intercomActive && intercomNodeId === nodeId ? (
              <Alert severity="success" sx={{ mb: 1 }}>
                Two-way intercom active. Dashboard mic audio selected camera ko ja raha hai.
              </Alert>
            ) : null}
            {selectedSession?.sessionId ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Session: {selectedSession.sessionId}
              </Typography>
            ) : null}
            {playbackUrl ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block", wordBreak: "break-all" }}
              >
                URL: {playbackUrl}
              </Typography>
            ) : null}
          </Grid>

          <Grid item xs={12} md={5}>
            <Card variant="outlined" sx={{ borderRadius: 1.5 }}>
              <CardContent sx={{ p: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.6 }}>
                  <ImageIcon fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Latest Snapshot
                  </Typography>
                </Stack>

                {snapshotState.loading ? (
                  <Typography variant="caption" color="text.secondary">
                    Snapshot refresh ho raha hai...
                  </Typography>
                ) : snapshotUrl ? (
                  <Box
                    component="img"
                    src={snapshotUrl}
                    alt={`${selectedNode.name}-snapshot`}
                    sx={{
                      width: "100%",
                      height: 140,
                      objectFit: "cover",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Snapshot not available yet.
                  </Typography>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.8, display: "block" }}>
                  {snapshot?.at ? `Captured: ${formatDateTime(snapshot.at)}` : "Capture time: -"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderActiveStreamsGrid = () => {
    if (activeSessionNodes.length === 0) {
      return (
        <Alert severity="info" variant="outlined">
          Abhi koi active stream nahi hai. CH1/CH2 start karoge to yahan dono live dikhenge.
        </Alert>
      );
    }

    return (
      <Grid container spacing={1.5}>
        {activeSessionNodes.map(({ node, nodeId, session, device }) => {
          const playbackUrl = String(session?.playbackUrl || "").trim();
          const renderPlaybackUrl = getRenderablePlaybackUrl(playbackUrl, session?.sessionId);
          const isMjpeg = isMjpegPlaybackUrl(playbackUrl);
          const inAction = actionNodeId === nodeId;

          return (
            <Grid item xs={12} md={6} key={nodeId}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 1.2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {node.name} | CH {node.channelId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {device?.name || asId(node.deviceId)}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PhotoCameraIcon />}
                        disabled={captureNodeId === nodeId}
                        onClick={() => handleTakeSnapshot(node)}
                      >
                        Snap
                      </Button>
                      <Button
                        size="small"
                        component="a"
                        href={playbackUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open
                      </Button>
                      <Button
                        size="small"
                        color={intercomActive && intercomNodeId === nodeId ? "error" : "success"}
                        variant={intercomActive && intercomNodeId === nodeId ? "contained" : "outlined"}
                        startIcon={intercomActive && intercomNodeId === nodeId ? <MicOffIcon /> : <MicIcon />}
                        disabled={
                          intercomSending || (!session?.sessionId && !(intercomActive && intercomNodeId === nodeId))
                        }
                        onClick={() =>
                          intercomActive && intercomNodeId === nodeId
                            ? handleStopIntercom(node)
                            : handleStartIntercom(node)
                        }
                      >
                        {intercomActive && intercomNodeId === nodeId ? "Stop Talk" : "Push To Talk"}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<StopIcon />}
                        disabled={inAction}
                        onClick={() => handleStopStream(nodeId)}
                      >
                        Stop
                      </Button>
                    </Stack>
                  </Stack>

                  <Box
                    sx={{
                      borderRadius: 1.5,
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "#0b1020",
                      minHeight: 180,
                    }}
                  >
                    {isMjpeg ? (
                      <img
                        src={renderPlaybackUrl}
                        alt={`${node.name}-ch${node.channelId}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    ) : (
                      <video
                        src={renderPlaybackUrl}
                        controls
                        autoPlay
                        muted
                        playsInline
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    )}
                  </Box>
                  {intercomActive && intercomNodeId === nodeId ? (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      Two-way talk active for this camera.
                    </Alert>
                  ) : null}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Camera Control Portal
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Device registration flow ke baad yahi se camera node register, start/stop aur live stream preview karo.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => loadPortalData({ silent: true })}
            disabled={refreshing || loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleStopAllStreams}
            disabled={loading || actionNodeId === "all" || Object.keys(sessionsByNode).length === 0}
          >
            Stop All
          </Button>
        </Stack>
      </Stack>

      {!!errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      {!!successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ py: 10, textAlign: "center" }}>
          <CircularProgress size={30} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                  Quick Register Camera
                </Typography>

                <Box component="form" onSubmit={handleRegisterNode}>
                  <Stack spacing={1.5}>
                    <Autocomplete
                      options={cameraDevices}
                      value={cameraDevices.find((d) => asId(d._id) === form.deviceId) || null}
                      onChange={(_, value) => {
                        const nextDeviceId = asId(value?._id || "");
                        setForm((prev) => ({ ...prev, deviceId: nextDeviceId }));
                        setSelectedDeviceId(nextDeviceId);
                      }}
                      getOptionLabel={(option) =>
                        `${option?.name || "Unnamed"} (${option?.imei || option?._id || "-"})`
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Device" required size="small" />
                      )}
                    />

                    <TextField
                      label="Camera Name"
                      value={form.name}
                      onChange={handleFormChange("name")}
                      placeholder="e.g. Front Camera"
                      required
                      size="small"
                    />

                    <TextField
                      label="Default Channel"
                      select
                      value={form.channelId}
                      onChange={handleFormChange("channelId")}
                      size="small"
                    >
                      <MenuItem value={1}>Channel 1</MenuItem>
                      <MenuItem value={2}>Channel 2</MenuItem>
                    </TextField>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <VideocamIcon />}
                        disabled={submitting}
                        fullWidth
                      >
                        Register Default
                      </Button>
                      <Button
                        type="button"
                        variant="outlined"
                        disabled={submitting}
                        onClick={(e) => handleRegisterNode(e, [1])}
                        fullWidth
                      >
                        Register CH1
                      </Button>
                      <Button
                        type="button"
                        variant="outlined"
                        disabled={submitting}
                        onClick={(e) => handleRegisterNode(e, [2])}
                        fullWidth
                      >
                        Register CH2
                      </Button>
                    </Stack>
                    <Button
                      type="button"
                      variant="outlined"
                      disabled={submitting}
                      onClick={(e) => handleRegisterNode(e, [1, 2])}
                    >
                      Register Both (CH1 + CH2)
                    </Button>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                  Active Streams ({activeSessionNodes.length})
                </Typography>
                {renderActiveStreamsGrid()}
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
              <CardContent>{renderStreamPreview()}</CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6" fontWeight={700}>
                    Camera Nodes ({visibleNodes.length})
                  </Typography>
                  <TextField
                    label="Device Filter"
                    size="small"
                    select
                    sx={{ minWidth: 280 }}
                    value={selectedDeviceId}
                    onChange={(e) => setSelectedDeviceId(e.target.value)}
                  >
                    {cameraDevices.map((device) => (
                      <MenuItem key={asId(device._id)} value={asId(device._id)}>
                        {device?.name || "Unnamed"} ({device?.imei || asId(device._id)})
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Divider sx={{ mb: 2 }} />

                {visibleNodes.length === 0 ? (
                  <Alert severity="info" variant="outlined">
                    Is device ke liye camera node register nahi hai.
                  </Alert>
                ) : (
                  <Stack spacing={1.2}>
                    {visibleNodes.map((node) => {
                      const nodeId = asId(node._id);
                      const session = sessionsByNode[nodeId];
                      const device = deviceById.get(asId(node.deviceId));
                      const isSelected = nodeId === selectedNodeId;
                      const inAction = actionNodeId === nodeId;

                      return (
                        <Box
                          key={nodeId}
                          sx={{
                            border: "1px solid",
                            borderColor: isSelected ? "primary.main" : "divider",
                            borderRadius: 2,
                            p: 1.2,
                            cursor: "pointer",
                            bgcolor: isSelected ? "action.hover" : "background.paper",
                          }}
                          onClick={() => setSelectedNodeId(nodeId)}
                        >
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            gap={1}
                          >
                            <Box>
                              <Typography fontWeight={700}>{node.name}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Device: {device?.name || asId(node.deviceId)} | Channel {node.channelId}
                              </Typography>
                              <Stack direction="row" spacing={0.6} sx={{ mt: 0.6 }}>
                                <Chip label={node.mode?.toUpperCase()} size="small" />
                                {session?.sessionId ? (
                                  <Chip label="Live" color="success" size="small" />
                                ) : (
                                  <Chip label="Idle" size="small" />
                                )}
                              </Stack>
                            </Box>

                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PhotoCameraIcon />}
                                disabled={captureNodeId === nodeId}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTakeSnapshot(node);
                                }}
                              >
                                Snapshot
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<PlayArrowIcon />}
                                disabled={inAction || Boolean(session?.sessionId)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartStream(nodeId);
                                }}
                              >
                                Start
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<StopIcon />}
                                disabled={!session?.sessionId || inAction}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStopStream(nodeId);
                                }}
                              >
                                Stop
                              </Button>
                            </Stack>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
